import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, LayersControl, useMap } from "react-leaflet";
import {
  uploadImageToCloudinary,
  fetchChatResponseWithImage,
} from "../utils/chatApi"; // Import the API utility
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-simple-map-screenshoter";
import "leaflet-sidebar-v2";
import "leaflet-sidebar-v2/css/leaflet-sidebar.min.css";
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import { GeneratePDFReport } from "../utils/pdfgenerate";
import { GenerateMobilePDFReport } from "../utils/pdfgenerate_mobile";
import markerIconPng from "../images/marker-icon.png";
import markerIcon2xPng from "../images/marker-icon-2x.png";
import markerShadowPng from "../images/marker-shadow.png";
import { Analytics } from "@vercel/analytics/react";
import { SearchBar } from "./SearchBar";
import { attachPegman } from "../utils/pegman";
import html2canvas from "html2canvas";
import { AnimatePresence } from "framer-motion";
import SplashScreen from "./SplashScreen";
import MiniMapControl from "./MiniMap";
import "../shims/proj4-setup";
import GeojsonGroupedControl from "./GroupedLayersControl";
import RasterGeoTiffOverlay from "./RasterGeoTiffOverlay";

declare module "leaflet" {
  interface Map {
    screenshoter?: any;
    sidebar?: any;
  }
}

let centerCoordinates: any; // Define the variable

const DEFAULT_CENTER = [37.7749, -122.4194]; // Default to San Francisco

(window as any).html2canvas = html2canvas;
// Load the last center from localStorage or use default
const storedCenter = localStorage.getItem("mapCenter");
const initialCenter: [number, number] = storedCenter
  ? JSON.parse(storedCenter)
  : DEFAULT_CENTER;

const InitializeMap: React.FC<{
  initializeMap: () => void;
  mapInstanceRef: React.MutableRefObject<L.Map | null>;
}> = ({ initializeMap, mapInstanceRef }) => {
  const map = useMap(); // Get map instance inside MapContainer

  React.useEffect(() => {
    mapInstanceRef.current = map; // Store reference to the map

    initializeMap(); // Call initialization logic
  }, [map]); // Runs when the map is available

  return null; // No UI needed
};
/* ✅ Define SearchControl Outside of MapComponent */

const SearchControl: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const customIcon = L.icon({
      iconUrl: markerIconPng,
      iconRetinaUrl: markerIcon2xPng,
      shadowUrl: markerShadowPng,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    // Use Photon as the geocoder for autocomplete suggestions
    const geocoder = (L.Control as any)
      .geocoder({
        defaultMarkGeocode: false,
        position: "topright",
        geocoder: new (L.Control as any).Geocoder.photon({
          serviceUrl: "https://photon.komoot.io/api/?limit=5", // Limit results to 5
        }),
      })
      .addTo(map);

    geocoder.on("markgeocode", function (event: any) {
      const { center, name } = event.geocode;

      // Remove existing markers before adding a new one
      map.eachLayer((layer) => {
        if ((layer as any).options?.icon) {
          map.removeLayer(layer);
        }
      });

      L.marker(center, { icon: customIcon })
        .addTo(map)
        .bindPopup(`<b>${name}</b>`, { closeButton: false })
        .openPopup();

      map.setView(center, 13);
    });

    return () => {
      map.removeControl(geocoder);
    };
  }, [map]);

  return null;
};

export { SearchControl };

const Map: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const drawnItems = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const [isReportGenerated, setIsReportGenerated] = useState(false);
  const [shapeData, setShapeData] = useState<{
    type: "polygon" | "circle" | null;
    center?: { lat: number; lng: number };
    coordinates?: { lat: number; lng: number }[];
    radius?: number;
    cloudinaryUrl?: string | undefined;
    mergedAnalysis?: string | undefined;
  } | null>(null);
  const [currentBaseMap, setCurrentBaseMap] = useState(
    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
  );
  const [mapCenter, setMapCenter] = useState<[number, number]>(initialCenter);

  const baseMaps = [
    {
      label: "Standard Map",
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    },
    {
      label: "Stadia Stamen Toner",
      url: "https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}{r}.png",
    },
    {
      label: "Topographic",
      url: "https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}",
    },
    {
      label: "Satellite",
      url: "http://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    },
    {
      label: "2024 USA Election",
      url: "2024-usa-election",
    },
  ];

  // L.DomEvent.disableClickPropagation(searchBarVar);
  // L.DomEvent.disableScrollPropagation(searchBarVar);
  const getColorForValue = (value: number | string) => {
    // Ensure value is a number
    const numericValue = typeof value === "string" ? parseFloat(value) : value;

    // Apply symbology rules
    if (numericValue <= -0.689025) return "#761d1f"; // Light Strong Republican Lead
    if (numericValue <= -0.45935) return "#7e3c3e"; // Light Heavy Republican Lead
    if (numericValue <= -0.229675) return "#865a5b"; // Light Moderate Republican Lead
    if (numericValue < 0) return "#8e7879"; // Light Slight Republican Lead
    if (numericValue == 0) return "#A9A9A9"; // Null values
    if (numericValue <= 0.210092) return "#74828b"; // Light Slight Democrat Lead
    if (numericValue <= 0.420184) return "#526e82"; // Light Moderate Democrat Lead
    if (numericValue <= 0.630276) return "#0b456b"; // Light Heavy Democrat Lead
    if (numericValue <= 0.840369) return "#0b456b"; // Light Strong Democrat Lead

    return "#cccccc"; // Default color for values outside the range
  };

  const initializeMap = () => {
    const map = mapInstanceRef.current;

    if (!map) return;

    // attachPegmanAndGoogle(
    //   map,
    //   { maxZoom: 24, type: "roadmap" },
    //   { position: "bottomright" }
    // );

    // Add the search control

    const drawControl = new L.Control.Draw({
      draw: {
        polygon: {
          shapeOptions: {
            color: "#ff0000",
            weight: 2,
          },
        },
        marker: false,
        polyline: false,
        rectangle: false,
        circle: {
          shapeOptions: {
            color: "#0000ff",
            weight: 2,
          },
        },
        circlemarker: false,
      },
      edit: {
        featureGroup: drawnItems.current,
      },
    });

    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, async (e: any) => {
      const type = e.layerType;
      const layer = e.layer;

      // Remove existing shapes before adding a new one
      drawnItems.current.clearLayers();
      drawnItems.current.addLayer(layer);

      async function getLocation(lat: number, lon: number) {
        const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=18&addressdetails=1`;

        try {
          const response = await fetch(url, {
            headers: { "User-Agent": "YourAppName/1.0" },
          });
          const data = await response.json();

          if (data && data.address) {
            // Extract smallest administrative unit
            const {
              suburb,
              neighbourhood,
              village,
              town,
              city,
              county,
              state,
              country,
            } = data.address;
            return (
              suburb ||
              neighbourhood ||
              village ||
              town ||
              city ||
              county ||
              state ||
              country ||
              "Unknown Location"
            );
          } else {
            return "No location found";
          }
        } catch (error) {
          console.error("Error fetching location:", error);
          return "Error retrieving location";
        }
      }

      if (type === "polygon") {
        const polygonLayer = layer as L.Polygon;
        const latLngs = polygonLayer.getLatLngs()[0] as L.LatLng[];
        // console.log("Polygon Coordinates:", latLngs);

        // Get polygon center using bounding box (not geometric centroid)
        const center = polygonLayer.getBounds().getCenter();
        center.lng = ((((center.lng + 180) % 360) + 360) % 360) - 180;
        const locationName = await getLocation(center.lat, center.lng);
        const currentZoom: number = map.getZoom();

        if (currentZoom >= 13) {
          centerCoordinates = locationName;
        } else {
          centerCoordinates = center;
        }

        const tabLink = document.querySelector(
          "a[href='#tab-3']"
        ) as HTMLAnchorElement;
        tabLink?.click();
        //Set location to local storage

        const newCenter: [number, number] = [center.lat, center.lng];
        setMapCenter(newCenter);
        localStorage.setItem("mapCenter", JSON.stringify(newCenter));

        setShapeData((prevData) => ({
          ...prevData,
          type: "polygon",
          center,
          coordinates: latLngs.map((point) => ({
            lat: point.lat,
            lng: point.lng,
          })),
        }));
      }

      if (type === "circle") {
        const circleLayer = layer as L.Circle;
        const center = circleLayer.getLatLng(); // Get center coordinates
        const radius = circleLayer.getRadius(); // Get radius in meters

        center.lng = ((((center.lng + 180) % 360) + 360) % 360) - 180;
        const locationName = await getLocation(center.lat, center.lng);

        const currentZoom: number = map.getZoom();
        console.log(currentZoom);

        if (currentZoom >= 13) {
          centerCoordinates = locationName;
        } else {
          centerCoordinates = center;
        }

        const tabLink = document.querySelector(
          "a[href='#tab-2']"
        ) as HTMLAnchorElement;
        tabLink?.click();
        //Set location to local storage
        const newCenter: [number, number] = [center.lat, center.lng];
        setMapCenter(newCenter);
        localStorage.setItem("mapCenter", JSON.stringify(newCenter));

        setShapeData((prevData) => ({
          ...prevData,
          type: "circle",
          center,
          radius,
        }));
      }

      if (window.innerWidth >= 576) {
        centerOnPolygon();
      }
    });
    map.addLayer(drawnItems.current);

    // Initialize SimpleMapScreenshoter
    const screenshoter = L.simpleMapScreenshoter({
      hidden: false,
      preventDownload: false,
      hideElementsWithSelectors: [".leaflet-control-container"],
      mimeType: "image/png",
    }).addTo(map);

    map.screenshoter = screenshoter;
    openPanel();
  };

  const map = mapInstanceRef.current;
  const [geoJsonLayer, setGeoJsonLayer] = useState<L.GeoJSON | null>(null);
  const [additionalLayer, setAdditionalLayer] = useState<L.TileLayer | null>(
    null
  );

  // Update the base layer when currentBaseMap changes
  useEffect(() => {
    if (map) {
      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          map.removeLayer(layer); // Remove existing tile layers
        }
      });
      if (currentBaseMap === "2024-usa-election") {
        // Load the GeoJSON layer for the 2024 USA Election
        loadGeoJsonData();

        // Add an additional layer (e.g., satellite imagery)
        const additionalTileLayer = L.tileLayer(
          "https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}{r}.png",
          {
            attribution: "© Esri",
          }
        ).addTo(map);

        setAdditionalLayer(additionalTileLayer); // Store the additional layer in state
      } else {
        // Add the selected base layer
        const selectedBaseMap = baseMaps.find(
          (baseMap) => baseMap.url === currentBaseMap
        );
        if (selectedBaseMap) {
          L.tileLayer(selectedBaseMap.url, {
            attribution: "© OpenStreetMap contributors",
          }).addTo(map);
        }

        // Remove the GeoJSON layer if it exists
        if (geoJsonLayer) {
          geoJsonLayer.remove();
          setGeoJsonLayer(null);
        }
      }
    }
  }, [currentBaseMap, map]);

  const loadGeoJsonData = async () => {
    try {
      const response = await fetch("/QGIS_ElectionPolygon.geojson");
      const geoJsonData = await response.json();
      addGeoJsonLayer(geoJsonData); // Add the GeoJSON layer to the map
    } catch (error) {
      console.error("Error loading GeoJSON data:", error);
    }
  };

  const addGeoJsonLayer = (geoJsonData: GeoJSON.FeatureCollection) => {
    if (geoJsonLayer) {
      geoJsonLayer.remove(); // Remove the existing GeoJSON layer if it exists
    }

    if (map) {
      const newGeoJsonLayer = L.geoJSON(geoJsonData, {
        style: (feature) => {
          // Check if feature is defined
          if (!feature || !feature.properties) {
            return {
              color: "#39FF14", // Default color for undefined features
              weight: 1,
              fillOpacity: 0.6,
            };
          }

          const meanPctDemLead = feature.properties.MEAN_pct_dem_lead || 0; // Use 0 as fallback
          const color = getColorForValue(meanPctDemLead); // Get color based on value
          return {
            color: color,
            weight: 1,
            fillOpacity: 0.7,
          };
        },
      }).addTo(map); // Add the new GeoJSON layer to the map

      setGeoJsonLayer(newGeoJsonLayer); // Update the state with the new GeoJSON layer
      console.log("GeoJSON loaded");
    }
  };
  const openPanel = () => {
    // Moving and hiding the buttons

    const hideOpenPanelButton = document.getElementById("openpanelbutton");
    const movebaseMapDropdown = document.querySelector("select");

    if (hideOpenPanelButton && movebaseMapDropdown) {
      hideOpenPanelButton.style.display = "none";
      movebaseMapDropdown.style.top = "10px";
    } else {
      console.log("Elements not present");
    }

    if (!mapInstanceRef.current) {
      console.error("Map instance not found.");
      return;
    }

    const sidebarElement = document.getElementById("sidebar");
    const map = mapInstanceRef.current;

    // Dynamically add the sidebar if not already present
    if (!map.sidebar) {
      const sidebar = new L.Control.Sidebar({ container: "sidebar" });
      map.addControl(sidebar);
      map.sidebar = sidebar;

      if (sidebarElement) {
        sidebarElement.style.display = "block"; // Make it render in the DOM
        const tabsContainer = document.querySelector(
          ".leaflet-sidebar-tabs"
        ) as HTMLElement;
        if (tabsContainer) {
          tabsContainer.style.backgroundColor = "#d3d3d3";
        }
        // Use a timeout to allow the browser to register the initial state before adding the transition class
        setTimeout(() => {
          sidebarElement.classList.add("visible");
        }, 10); // A small delay to ensure the transition applies
      } else {
        console.error("Sidebar element not found.");
      }
    }

    //Select all check boxes
    // Get the "Select All" checkbox
    const selectAllCheckbox = document.getElementById("selectAll");

    // Get all the checkboxes with the name "topic"
    const topicCheckboxes = document.querySelectorAll('input[name="topic"]');

    // Check if the "Select All" checkbox exists
    if (selectAllCheckbox) {
      // Add an event listener to the "Select All" checkbox
      selectAllCheckbox.addEventListener("change", function () {
        // Loop through all the topic checkboxes
        topicCheckboxes.forEach((checkbox) => {
          // Set the checked state of each checkbox to match the "Select All" checkbox
          (checkbox as HTMLInputElement).checked = (
            selectAllCheckbox as HTMLInputElement
          ).checked;
        });
      });
    } else {
      console.error("Select All checkbox not found");
    }
    // Leaflet Panel Position
    const sidebarWidth = sidebarElement?.getBoundingClientRect().width || 0;
    const screenshoter = map.screenshoter;
    const leafletPanel = document.querySelector(
      ".leaflet-top.leaflet-left"
    ) as HTMLElement;

    if (leafletPanel) {
      leafletPanel.style.left = `${sidebarWidth + 10}px`;
    } else {
      console.error("Leaflet panel not found.");
    }

    // somewhere after your sidebar is laid out, before you ever open Street-View:
    function injectPegmanRepositionCSS(offsetPx: number) {
      // guard against injecting twice
      if (document.getElementById("pegman-reposition")) return;

      const css = `
    /* push Pegman’s panorama over by offsetPx */
    .gm-iv-container {
      left: ${offsetPx}px !important;

    }
  `;
      const style = document.createElement("style");
      style.id = "pegman-reposition";
      style.innerHTML = css;
      document.head.appendChild(style);
    }

    // Example: once your sidebarWidth is known:
    injectPegmanRepositionCSS(sidebarWidth + 10);

    // Prevent duplicate content
    const tab1 = document.getElementById("tab-1");
    if (tab1 && !tab1.querySelector(".info-added")) {
      const tab1Content = `
              <div class="info-added">
            
                 <div>
  
      <header>
        <h1 >RelocateIQ</h1>
        <h3 >
          Discover, Analyze and Download!
        </h3>
     
      </header>

      <section >
        <h2 >🌟 Key Features</h2>
        <ul >
          <li> <strong>Search for Locations</strong> – Find places instantly with OpenStreetMap Search.</li>
          <li><strong>Draw & Annotate Maps</strong> – Highlight areas with shapes and markers.</li>
          <li> <strong>Generate Reports</strong> – Create structured reports based on map data.</li>
          <li> <strong>Download as PDF</strong> – Save and share reports easily.</li>
        </ul>
      </section>

     
      <section >
        <h2 >📌 How to Use</h2>
        <p>1️⃣ Use the search bar to find a location.</p>
        <p>2️⃣ Click on the circle or polygon icon to highlight.</p>
        <p>3️⃣ Select your topics of interest.</p>
        <p>4️⃣ Click <strong>“Generate Report”</strong> to create an analysis.</p>
        <p>5️⃣ Once ready, click <strong>“Download Report”</strong> to get your PDF.</p>

       
      </section>


    
    </div>
<div>
  <h2>💡 Tips</h2>
  <ul>
    <li><strong>Include full names</strong> of neighborhoods and street names in the shape for more accurate results.</li>
    <li><strong>Select fewer topics</strong> to generate your report faster.</li>
        <li>Keep the shape <strong>visible and within the bounds</strong> of the screen.</li>
          <li><strong>Zoom in closer  </strong> to get details on a specific neighborood.</li>
           <li><strong>Best viewed  </strong> on a desktop or laptop.</li>

  </ul>
</div>
<br/>
                <p>  ⚠️ <strong>Disclaimer:</strong> The results provided are generated using ChatGPT, an AI language model. While we strive for accuracy, the information may contain errors, omissions, or outdated details. This content should not be considered professional, legal, financial, or medical advice. Always verify critical information with authoritative sources before making decisions based on this output.</p>
              </div>
                <footer >
        <p>© 2025 RelocateIQ. All Rights Reserved.</p>
      </footer>
          `;

      tab1.innerHTML += tab1Content;
    }

    // Custom Button Positions
    if (screenshoter) {
      map.sidebar.open("tab-1");

      // Adjust button and dropdown positions
      const screenshotButton = document.querySelector("button");
      const baseMapDropdown = document.querySelector("select");
      const searchBarBox =
        document.querySelector<HTMLElement>(".searchBarClass");

      if (screenshotButton) {
        screenshotButton.style.left = `${sidebarWidth + 65}px`;
      }

      if (baseMapDropdown) {
        baseMapDropdown.style.left = `${sidebarWidth + 65}px`;
        if (window.innerWidth <= 575) {
          baseMapDropdown.style.cssText += `top: 10px !important;`;
        }
      }
      if (searchBarBox) {
        searchBarBox.style.left = `${sidebarWidth + 65}px`;
        if (window.innerWidth <= 575) {
          searchBarBox.style.cssText += `display: none !important;`;
        }
      }

      // const dropdown = document.querySelector('.select-dropdown');

      // if (window.innerWidth <= 575) {
      //   baseMapDropdown.style.top = '0px';
      // } else {
      //   baseMapDropdown.style.top = '55px'; // Reset to original position if needed
      // }
    }

    // Add an event listener for the dynamically created button
    const generateButton = document.getElementById("generate-report");
    if (generateButton) {
      generateButton.addEventListener("click", handleGenerateReport);
    }
  };

  const centerOnPolygon = () => {
    if (!mapInstanceRef.current || !drawnItems.current) {
      console.error("Map or drawn items not found.");
      return;
    }

    const map = mapInstanceRef.current;
    const bounds = drawnItems.current.getBounds();

    if (!bounds.isValid()) {
      alert("No polygon drawn to center on!");
      return;
    }

    map.fitBounds(bounds, { padding: [20, 20] });
  };

  const handleBaseMapChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedBaseMap = baseMaps.find(
      (baseMap) => baseMap.url === event.target.value
    );
    if (selectedBaseMap) {
      setCurrentBaseMap(selectedBaseMap.url);
    }
  };

  const generateCollapsibleSections = (
    topics: string[],
    analysisData: Record<string, string>,
    labeltext: string[]
  ): void => {
    const resultsContent = document.getElementById("tab-3");
    if (resultsContent) {
      resultsContent.innerHTML = ""; // Clear previous content
      topics.forEach((topic, index) => {
        const section = document.createElement("div");
        section.className = "collapsible-section";

        // Create collapsible button
        const button = document.createElement("button");
        button.className = "collapsible";
        button.textContent = labeltext[index] || topic;
        // button.textContent = topic;
        button.addEventListener("click", () => {
          button.classList.toggle("active");
          const content = button.nextElementSibling as HTMLElement | null;

          if (content) {
            if (content.style.maxHeight && content.style.maxHeight !== "0px") {
              content.style.maxHeight = "0px"; // Collapse
            } else {
              content.style.maxHeight = `${content.scrollHeight + 50}px`; // Expand
            }
          }
        });

        // Create content area with actual analysis data
        const content = document.createElement("div");
        content.id = `content-${topic.replace(/\s+/g, "-")}`; // Assign unique ID
        content.className = "collapsible-content";
        content.style.maxHeight = "0px"; // Ensure it's collapsed initially
        content.style.overflow = "hidden"; // Hide overflowing content during animation
        content.style.transition = "max-height 0.5s ease-in-out";

        // Helper function to format analysis text
        const formatAnalysisText = (text: string): string => {
          // ✅ Remove all line breaks from the input text
          const singleLineText = text.replace(/\n+/g, " ");

          // ✅ Remove numbered lists like "1.", "2.", etc. at the start of a sentence
          const cleanedText = singleLineText.replace(/^\d+\.\s+/gm, "");

          // ✅ Remove double asterisks
          const noAsterisksText = cleanedText.replace(/\*\*/g, "");

          // ✅ Improved regex for intelligent sentence splitting (ignores "U.S.", etc.)
          const sentenceRegex =
            /(?<!\b[A-Z][a-z]\.)(?<!\b[A-Z]\.)(?<!\b[A-Z][A-Z]\.)(?<=[.!?])\s+(?=[A-Z])/g;

          const sentences = noAsterisksText.split(sentenceRegex) || []; // Split intelligently
          const sentencesPerParagraph = 3; // Group sentences into paragraphs

          return sentences
            .reduce((acc, sentence, index) => {
              if (index % sentencesPerParagraph === 0) {
                acc.push([]);
              }
              acc[acc.length - 1].push(sentence);
              return acc;
            }, [] as string[][])
            .map((group) => `<p>${group.join(" ")}</p>`)
            .join("\n");
        };

        let isSpeaking = false;
        let selectedVoice: SpeechSynthesisVoice | null = null;

        // Detect if the user is on an Android device
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isAndroid = /Android/i.test(navigator.userAgent);
        const isIphone = /iPhone|iPad|iPod/i.test(navigator.userAgent);

        function loadVoices() {
          const voices = speechSynthesis.getVoices();

          if (isMobile) {
            // Try to get a specific Google voice, otherwise fallback to default
            selectedVoice =
              voices.find((voice) => voice.name === "Google UK English Male") ||
              voices.find((voice) => voice.default) ||
              null;
          } else if (isIphone) {
            // Always use the default system voice on iOS
            selectedVoice = voices.find((voice) => voice.default) || null;
          } else {
            // Prioritize UK English voices, then fallback to the system default
            selectedVoice =
              voices.find((voice) => voice.name === "Google UK English Male") ||
              voices.find((voice) => voice.lang === "en-GB") ||
              voices.find((voice) => voice.default) ||
              null;
          }

          return selectedVoice;
        }

        // Ensure voices are loaded
        window.speechSynthesis.onvoiceschanged = loadVoices;

        // Function to remove text enclosed in parentheses
        function removeParentheses(text: string) {
          return text.replace(/\(.*?\)/g, "");
        }

        function truncateAtSources(text: string) {
          const lastIndex = text.lastIndexOf("Sources:");
          return lastIndex !== -1 ? text.substring(0, lastIndex).trim() : text;
        }
        // Function to split text into sentences (for Android)
        function splitIntoSentences(text: string) {
          return text.match(/[^.!?]+[.!?]+/g) || [text]; // Split at ., !, or ? while keeping punctuation
        }

        // Stop speech if it's already ongoing
        function stopSpeaking() {
          speechSynthesis.cancel();
          isSpeaking = false;

          // Hide the stop button when speech stops
          const stopButton = document.getElementById("stopButton");
          if (stopButton) {
            stopButton.style.display = "none";
          }
        }

        // Function to read the analysis content aloud
        function speakAnalysis(
          topic: string,
          rate = 1.0,
          pitch = 1.0,
          volume = 1.2
        ) {
          if (!analysisData || !analysisData[topic]) {
            console.error(`Analysis content not found for topic: ${topic}`);
            return;
          }

          // Stop any ongoing speech
          stopSpeaking();

          const allTitles = document.querySelectorAll(".analysis-content h3");
          let contentElement = null;

          // Find the correct title matching the topic
          allTitles.forEach((title) => {
            if (title.textContent?.includes(`${topic} Analysis`)) {
              contentElement = title.parentElement;
            }
          });

          if (!contentElement) {
            console.error(`Could not find content element for topic: ${topic}`);
            return;
          }

          let textToRead = analysisData[topic];
          textToRead = removeParentheses(textToRead);
          textToRead = truncateAtSources(textToRead);

          if (textToRead) {
            const stopButton = document.getElementById("stopButton");
            if (stopButton) {
              stopButton.style.display = "block"; // Show stop button when speech starts
            }

            if (isMobile) {
              // 📱 **Android-Specific Chunking Fix**
              const sentences = splitIntoSentences(textToRead);
              let sentenceIndex = 0;

              function speakNextSentence() {
                if (sentenceIndex >= sentences.length || !isSpeaking) {
                  isSpeaking = false;
                  stopSpeaking();
                  return;
                }

                const utterance = new SpeechSynthesisUtterance(
                  sentences[sentenceIndex]
                );
                utterance.lang = "en-GB";
                utterance.rate = rate;
                utterance.pitch = pitch;
                utterance.volume = volume;

                if (selectedVoice) {
                  utterance.voice = selectedVoice;
                }

                utterance.onend = () => {
                  sentenceIndex++;
                  setTimeout(speakNextSentence, 300); // Slight delay for smoother transitions on Android
                };

                utterance.onerror = (e) => {
                  console.error("Speech synthesis error:", e);
                  stopSpeaking();
                };

                speechSynthesis.speak(utterance);
              }

              isSpeaking = true;
              speakNextSentence(); // Start sentence-by-sentence speech for Android
            } else {
              // 💻 **Standard Pause/Resume for Desktop & iOS**
              if (isSpeaking) {
                stopSpeaking();
                return;
              }

              const utterance = new SpeechSynthesisUtterance(textToRead);
              utterance.lang = "en-GB";
              utterance.rate = rate;
              utterance.pitch = pitch;
              utterance.volume = volume;

              if (selectedVoice) {
                utterance.voice = selectedVoice;
              }

              isSpeaking = true;
              speechSynthesis.speak(utterance);

              // Auto-pause and resume speech every 14 seconds to prevent cutting off
              let resumeInterval = setInterval(() => {
                if (!speechSynthesis.speaking) {
                  clearInterval(resumeInterval);
                } else {
                  speechSynthesis.pause(); // Pause first
                  setTimeout(() => speechSynthesis.resume(), 100); // Resume after 100ms
                }
              }, 13000);

              // Stop interval when speech ends
              utterance.onend = () => {
                clearInterval(resumeInterval);
                stopSpeaking();
              };
            }
          }
        }

        // Ensure voices are loaded initially
        loadVoices();

        // Add Stop Button to the page

        const stopButton = document.createElement("button");
        stopButton.id = "stopButton";
        stopButton.textContent = "Stop Playback";
        stopButton.style.position = "fixed";
        stopButton.style.top = "50px";
        stopButton.style.right = "10px";
        stopButton.style.padding = "10px 15px";
        stopButton.style.background = "#ff4444";
        stopButton.style.color = "#fff";
        stopButton.style.border = "none";
        stopButton.style.borderRadius = "5px";
        stopButton.style.cursor = "pointer";
        stopButton.style.zIndex = "1000";
        stopButton.style.display = "none";

        if (window.innerWidth <= 575) {
          stopButton.style.top = "65px";
        }
        stopButton.addEventListener("click", stopSpeaking);

        document.body.appendChild(stopButton);

        function copyAnalysis(topic: string) {
          if (!analysisData || !analysisData[topic]) {
            console.error(`Analysis content not found for topic: ${topic}`);
            return;
          }

          const textToCopy = analysisData[topic];

          navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
              console.log(`Copied analysis for topic: ${topic}`);
            })
            .catch((err) => {
              console.error("Failed to copy analysis:", err);
            });
        }

        // Use analysis data or fallback message
        let analysisContent = analysisData[topic]
          ? `<div class="analysis-content">
    <h3 class="analysis-title">${topic} Analysis
      <button class="speaker-button" onclick="speakAnalysis('${topic}')">🔊</button>
              <button class="copy-button" onclick="copyAnalysis('${topic}')">📋</button>
           

    </h3> 
    ${formatAnalysisText(analysisData[topic])}
  </div>`
          : `<p class="no-analysis">  Unable to generate analysis for ${topic}...</p>`;

        // const convertUrlsToLinks = (text: string): string => {
        //   return text.replace(
        //     /(\bhttps?:\/\/[^\s<)]+)(?=[\s)<]|$)/gi, // Exclude trailing punctuation if not part of the URL
        //     '<a href="$1" target="_blank" rel="noopener noreferrer">Source</a>'
        //   );
        // };
        // Apply URL replacement to analysisContent
        // analysisContent = convertUrlsToLinks(analysisContent);

        // const convertUrlsToLinks = (text: string) => {
        //   // Ensure we don't modify already converted <a> tags
        //   if (/<a\s+href=["']https?:\/\/[^"']+["'].*?>.*?<\/a>/.test(text)) {
        //     return text;
        //   }

        //   return (
        //     text
        //       // Convert markdown-style links to proper anchor tags (only if valid URL inside `()`)
        //       .replace(
        //         /\[([^\]]+)\]\(\s*(https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}([^\s<>"'()]*)?)\s*\)/g,
        //         '<a href="$2" target="_blank">$1</a>'
        //       )
        //       // Convert standalone URLs into clickable links, ensuring they don't include `</p>` or other unwanted tags
        //       .replace(
        //         /(?<!href=")(https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}([^\s<>"'()]*)?)(?=\s|$|[.,?!<\/])/g,
        //         '<a href="$1" target="_blank">$1</a>'
        //       )
        //   );
        // };
        // Apply URL replacement to analysisContent
        // analysisContent = convertUrlsToLinks(analysisContent);

        content.innerHTML = analysisContent;

        // // Add elements to DOM
        (window as any).speakAnalysis = speakAnalysis;
        (window as any).copyAnalysis = copyAnalysis;
        section.appendChild(button);
        section.appendChild(content);
        resultsContent.appendChild(section);
      });

      // Open the first section by default
      const firstButton = resultsContent.querySelector(
        ".collapsible"
      ) as HTMLButtonElement | null;
      if (firstButton) {
        const firstContent =
          firstButton.nextElementSibling as HTMLElement | null;
        if (firstContent) {
          firstButton.classList.add("active");
          firstContent.style.maxHeight = `${firstContent.scrollHeight}px`;
        }
      }
    } else {
      console.error("Results container not found.");
    }
  };

  // Add these refs at the top of your component
  const previousAnalysisRef = useRef<Record<string, string>>({});
  const previousGeometryRef = useRef<string>("");
  const lastCloudinaryUrlRef = useRef<string>("");

  //Change here to parse data
  const parseChatResponse = (response: string): Record<string, string> => {
    const result: Record<string, string> = {};

    // Remove introductory disclaimers before the first "Topic:"
    response = response.replace(/^.*?(?=Topic:)/s, "").trim();

    // Updated regex to handle "Topic: X Analysis: ..." format in a single line
    const topicBlocks = response.match(
      /Topic:\s*(.*?)\s*Analysis:\s*([\s\S]*?)(?=\n\nTopic:|\n*$)/g
    );

    if (topicBlocks) {
      topicBlocks.forEach((block) => {
        const topicMatch = block.match(
          /Topic:\s*(.*?)\s*Analysis:\s*([\s\S]*)/
        );
        if (topicMatch) {
          const topic = topicMatch[1].trim();
          let analysis = topicMatch[2].trim();

          // Replace all instances of "source" (case-insensitive) with "SOURCE"
          // and remove any surrounding brackets.
          analysis = analysis.replace(/\[source\]/gi, "(Source)"); // Converts [Source] → (Source)

          //Replaces all brackets
          analysis = analysis.replace(/\[.*?\]/g, ""); // Removes all brackets and their contents

          result[topic] = analysis;
        }
      });
    }

    return result;
  };

  // Add state for loading at the component level

  const [isLoading, setIsLoading] = useState(false);
  const handleGenerateReport = async () => {
    let topicProcessed: number = 0;
    console.log("Generate Report button clicked");

    const map: L.Map | null = mapInstanceRef.current;
    // const screenshoter = map.screenshoter;
    // const map: L.Map | null = mapInstanceRef.current;
    if (!map) {
      console.error("Map instance is not available.");
      return;
    }

    const screenshoter = map.screenshoter;
    // Get selected topics from form
    const topicQuestions: Record<string, string[]> = {
      Demographics: [
        "What are the key demographic trends in this area over the past decade?",
        "How diverse is the population in terms of ethnicity, socioeconomic background, and language spoken?",
        "What is the age distribution of the population, and how does it impact local policies, businesses, and community dynamics?",
        "What are the racial groups that can be found here?",
        "How do migration patterns affect the population growth and demographics of this area?",
        "Are there any notable disparities in income, education, or employment rates among different demographic groups?",
      ],
      Environment: [
        "What is the overall climate and seasonal weather patterns in this region?",
        "Are there any environmental concerns such as pollution, deforestation, or water quality issues?",
        "How does the region handle sustainability, waste management, and conservation efforts?",
        "How is this region preparing for the long-term impacts of climate change, including extreme weather, rising sea levels, and resource availability?",
        "Is the area prone to natural disasters (flooding, hurricanes, earthquakes, wildfires), and how well-prepared is the region to handle them?",
        "What are the primary sources of energy used in this area, and how does it impact the environment?",
      ],

      Business: [
        "What are the major industries and economic drivers in this region?",
        "How business-friendly is the area in terms of regulations, tax policies, and infrastructure?",
        "What are the emerging business trends, and are there any notable startups or entrepreneurial activities?",
        "What are the key industries or sectors in this area that offer the most opportunities for small businesses?",
        "How accessible are funding options such as small business loans, venture capital, and grants in this region?",
        " What are the main legal and regulatory requirements for starting and operating a business in this area?",
        "What is the level of competition and market demand for small businesses in this region?",
      ],
      Healthcare: [
        "What is the quality of healthcare facilities in this area?",
        "Are there specialized medical services available in this area, such as cardiology or oncology centers and others?",
        "What are the typical healthcare costs and insurance coverage options for residents in this area?",
        "How crucial is access to quality healthcare facilities for people living in this area?",
        "What is the life expectancy of this area?",
      ],
      Education: [
        "List the top-ranked schools and universities in this area based on academic performance, student satisfaction, and faculty credentials.",
        "Compare the standardized test scores, student-teacher ratios, and graduation rates of public and private schools in this area.",
        "Provide an overview of homeschooling networks, virtual academies, and charter schools in this area, including their accessibility and enrollment options.",
        "What is the literacy rate in this region, and how does it compare to national and global averages?",
        "What programs or initiatives exist to improve literacy among children and adults, particularly in underserved communities?",
      ],

      Housing: [
        "What is the median household income of this location?",
        "What types of housing are most available in this area, and how does supply meet current demand?",
        "What are the key property tax rates and zoning regulations that impact homeownership and development?",
        " Are there government programs or incentives for affordable housing and first-time homebuyers?",
        " What are the recent trends in the real estate market, including housing demand and price fluctuations?",
      ],
      Proximity: [
        "How accessible are key locations such as airports and city centers?",
        "What is the average commute time for residents?",
        "Is this area walkable or bikeable or is it better suited for cars?",
        "Are public transportation options reliable and available?",
        "What are the nearest major airports?",
        "What are the nearest major expressways?",
        "What are the major shopping centers found in this area?",
      ],
      Work: [
        "What are the major employers or industries driving the local economy in this area?",
        "How diverse is the job market in this area across different industries?",
        "Are there emerging industries or growing sectors in this area?",
        "What is the unemployment rate and job availability like in this area?",
        "How does the average salary in this area compare to the cost of living?",
      ],
      Family: [
        "What family-friendly amenities are available in this area?",
        "How do local schools and childcare services in this area compare in terms of quality and accessibility?",
        "Are there community activities and events designed for families in this area?",
        "How abundant are senior care support and services in this area?",
      ],
      Lifestyle: [
        "What types of recreational activities and outdoor opportunities are available in this area?",
        "Could this area be described as fast-paced or more relaxed?",
        "Are there vibrant cultural or artistic communities in this area?",
      ],
      Community: [
        "How could the overall sense of community be described in this area?",
        "What social events, festivals, or gatherings are commonly held in this area?",
        "What languages are commonly spoken in this area?",
      ],
      Safety: [
        "What is the crime rate in this area, and how does it compare to nearby regions?",
        "Are there specific safety concerns or crime trends that residents should be aware of in this area?",
        "How effective and responsive is local law enforcement in maintaining safety?",
        "How is the rate of violent crime and property theft in this area?",
        "Does this area have a history of racial crime, including factors such as sundown towns, racially motivated crimes, police brutality, and incidents of racial injustice?",
        "How safe is it for a woman to be out in this area by themselves and is there a history of sexual harrassment prevalent to the area?",
      ],
      Immigration: [
        "Are migration patterns higher for people moving into this area compared to surrounding regions, and what factors contribute to this difference?",
        "What is the trend in population growth over the past decade, and how has internal migration influenced this change?",
        "What are the key economic and social factors driving internal migration to or from this area?",
        "What are the legal and administrative requirements for relocating to this county/state/region from another part of the country?",
        "Are there policies or incentives in place to attract new residents, such as tax benefits, job programs, or housing assistance?",
      ],
      Gun: [
        "What are the regulations regarding personal protection tools in this region?",
        "Are there specific guidelines for carrying items intended for protection?",
        "How do local laws address the use of tools for safeguarding oneself?",
      ],
      Race: [
        "Does this area have a documented history of racially motivated violence?",
        "Are there known cases of sundown towns or racial exclusion zones in this area?",
        "How diverse and inclusive is the local population in terms of race, culture, and community representation?",
      ],
      Pets: [
        "What are the local regulations regarding pet ownership in this area?",
        "Are there pet-friendly housing options available for renters and homeowners in this area?",
        "What is availability of veterinary services, pet stores, and other amenities for pet owners in this area, are they spare or abundant?",
      ],
      Technology: [
        "What is the average internet speed and availability of service providers in this area?",
        "Are there tech hubs, coworking spaces, or other resources for remote workers in this area?",
        "What initiatives or developments exist regarding smart city infrastructure in this region?",
        "How reliable is internet connectivity and access to technology in this area?",
      ],
      Sustainability: [
        "What are the local policies on renewable energy adoption and environmental conservation in this area?",
        "How does the city manage waste disposal, recycling programs, and sustainability efforts?",
        "Are there sufficient green spaces, parks, and eco-friendly initiatives in this area?",
      ],
      Entertainment: [
        "What are the must-see landmarks and attractions in this area?",
        "Which museums, galleries, or cultural sites are worth visiting in this area?",
        "What local restaurants or signature dishes is this area known for?",
        "Where do locals typically spend their weekends or free time in this area?",
        "Are there any hidden gems, such as a secret beach, street art alley, or unique local shop, that visitors often overlook?",
      ],
      Freedom: [
        "Are there any laws restricting criticism of the government, political figures, or public institutions, and how are they enforced in this county/state/region?",
        "What legal protections exist for journalists, whistleblowers, and independent media against government retaliation or censorship?",
        "Are news outlets able to operate freely without government control, and how common is self-censorship due to fear of repercussions in this county/state/region?",
        "How does the government handle protests, public demonstrations, or online speech that criticizes policies or officials?",
        "  Are there independent organizations that monitor press freedom, and what do their reports say about media freedom in this location?",
      ],

      Transportation: [
        "How reliable and accessible is public transportation in this area?",
        "What are the typical traffic conditions and peak commute times in this area?",
        "Are there walkable neighborhoods or bike-friendly infrastructure available in this area?",
        "How affordable are public transportation options compared to owning a car in this area?",
        "Are there any future plans or developments aimed at improving transportation infrastructure in this area?",
      ],
      // ],
      Tax: [
        "What are the average income and property tax rates in this area compared to neighboring regions?",
        "How does the cost of living in this area compare to similar locations?",
        "What are the major economic drivers and job opportunities available in this area?",
        "How well-developed is the infrastructure, including roads, utilities, and public services, in this area?",
        "What is the overall quality of life in this area based on factors like safety, amenities, and community engagement?",
      ],
      Expat: [
        "Does this area have a well-established expatriate or nomadic community?",
        "Are there co-living spaces, digital nomad hubs, or international networking opportunities in this area?",
        "How welcoming is this area to expatriates and long-term travelers in terms of culture, services, and integration?",
        "What resources or support systems exist for expatriates and digital nomads living in this area?",
        "What visa options are available to digital nomads and retirees and expats for this state/region/country?",
        "What is the ranking on the latest Global Peace Index report?",
      ],
      Governance: [
        "How active is the local community in governance and civic engagement in this area?",
        "Are there opportunities for residents to participate in local decision-making, such as town halls or community boards?",
        "What is the general political climate of this area, and how does it influence local policies and governance?",
        "How diverse are political perspectives in this area, and is there a dominant political affiliation among residents?",
        "Are there any notable grassroots movements or advocacy groups in this area that influence local policies?",
      ],

      Cultural: [
        "How is the balance of different cultural and religious influences in this area?",
        "Are there any particular cultural or religious traditions celebrated in this area?",
        "Do people of different faiths and cultural backgrounds generally coexist peacefully in this area?",
        "How visible is the presence of different religious institutions and cultural centers in this area?",
      ],

      Recreation: [
        "How prevalent are legal dispensaries in the area?",
        "What is the general attitude toward legal dispensaries in the area?",
        "Are there specific regulations or restrictions on dispensaries in the area?",
        "How accessible are legal dispensaries to residents in the area?",
      ],
      Uniqueness: [
        "What defining natural or man-made features, industries, or traditions set this location apart from others?",
        "How has history, culture, and local lifestyle shaped the way people experience this place today?",
        "What common perceptions or stereotypes exist about this area, and how accurate are they?",
        "How do residents and visitors perceive safety, community, and overall quality of life here?",
        "How has media, art, or local expression influenced the identity and reputation of this place?",
      ],
      Wildlife: [
        "What is the diversity of wildlife species in this region, including any endemic or endangered species?",
        "Are there any major threats to wildlife, such as habitat loss, poaching, or invasive species?",
        "How does the region manage wildlife conservation and protect natural habitats?",
        "What is the impact of human activities on local wildlife populations and their ecosystems?",
        "Are there any unique ecological interactions or keystone species that play a critical role in this area's biodiversity?",
        "Is the region facing challenges related to wildlife migration, climate change effects, or disease outbreaks among species?",
      ],
      Pollution: [
        "What are the most common chronic illnesses in this region, and how are they linked to environmental pollution?",
        "Which pollutants (air, water, or soil) are most prevalent in this area, and what are their known health effects?",
        "How does long-term exposure to pollution contribute to respiratory diseases, cardiovascular conditions, or other chronic health issues in local populations?",
        "What policies or mitigation strategies exist to reduce pollution-related health risks, and how effective have they been?",
        "Are there specific vulnerable populations (e.g., children, elderly, low-income communities) disproportionately affected by pollution-related chronic illnesses in this region?",
      ],
      Geopolitics: [
        "What are the key geopolitical dynamics influencing this region, including historical conflicts or alliances?",
        "How do this region's foreign relations impact its trade, economy, and diplomatic standing on the global stage?",
        "What are the major security concerns in this area, such as military tensions, border disputes, or political instability?",
        "How does this region engage with international organizations (e.g., UN, NATO, BRICS, ASEAN), and what role does it play in global affairs?",
        "What are the most significant emerging geopolitical trends or shifts that could affect this region in the near future?",
      ],
      Colonial: [
        "Are there modern forms of neocolonialism affecting this region, such as economic dependence, debt traps, or foreign corporate control?",
        "What lasting effects of colonialism are still visible in this region's legal system, economy, or cultural identity?",
        "How have colonial-era land policies affected wealth distribution, indigenous rights, and rural development?",
        "How did this region transition from colonial rule to independence, and what challenges did it face?",
        "How has colonial rule shaped the political, economic, and social structures of this region?",
        "What were the primary colonial powers that controlled this region, and how did they govern?",
      ],
      Poverty: [
        "What is the overall income distribution in this region, and how significant is the wealth gap?",
        "What percentage of the population lives below the poverty line, and how has this changed over time?",
        "What are the primary factors contributing to income inequality in this area?",
        "How accessible are essential services such as healthcare, education, and affordable housing for low-income individuals?",
        "What policies or initiatives exist to address poverty and economic inequality, and how effective have they been?",
      ],
      BRICS: [
        "How does this region engage economically with BRICS countries, and what are the key trade partnerships?",
        "What is the level of foreign direct investment (FDI) from BRICS nations in this area, and in which sectors?",
        "How have BRICS-related initiatives or agreements influenced infrastructure development and economic growth in this region?",
        "What geopolitical or diplomatic relationships exist between this region and BRICS member states?",
        "How does the local population perceive BRICS' influence on the economy, job market, and overall development?",
      ],
      Future: [
        "Is the region experiencing population growth or decline, and how does this affect housing, jobs, and public services?",
        "What major infrastructure or urban development projects are planned, and how might they impact the local economy and community? Goive specific example if available.",
        "Which industries or sectors are expected to grow, and are any at risk of decline or job loss?",
        "How is the local government preparing for both economic expansion and potential downturns (e.g., job losses, business closures, population decline)?",
        "What are the biggest challenges to future development, such as environmental risks, economic instability, or shifts in population trends?",
      ],
      Happiness: [
        "How does the overall quality of life in this region compare to national or global happiness indexes?",
        "How does the region's cost of living, job opportunities, and work-life balance impact overall psychological well-being?",
        "What are the primary sources of stress or dissatisfaction among residents, and how are they being addressed??",
        "How do community engagement, social support systems, and cultural attitudes influence residents' overall happiness and emotional resilience?",
        "What is the prevalence of mental health challenges, such as anxiety, depression, or crises, in this area, and how does it compare to national or regional averages?",
      ],
      Relationships: [
        "What are the cultural norms and expectations around dating, friendships, and family dynamics in this area?",
        "How does the local social scene (e.g., nightlife, community events, or social groups) influence relationship-building opportunities?",
        "Are there significant trends in marriage, divorce, or cohabitation rates, and how do they compare to national or regional averages?",
        "How do factors like migration, economic conditions, or demographic shifts affect relationships and social cohesion in this region?",
        "How easy is it to form meaningful connections outside of work or academic settings as an international resident?",
      ],
      CurrencyUSD: [
        "What’s the effective exchange rate today for cash, card, and bank transfers—and how far is each from the mid-market rate?",
        "How much of a small essentials basket does $100 buy right now vs. last year and vs. a neighboring country?",
        "Are big-ticket prices (rent, cars, electronics) quoted/settled in USD/EUR, and are monthly adjustments tied to FX?",
        "Can I freely move money in and out (ATM limits, card acceptance, wire/crypto on-ramps), and what’s the total friction cost?",
        "What’s the gap between the official and parallel/street rate, and how volatile has the rate been over the last 30–90 days?",
      ],
      Gangs: [
        "What forms of trafficking (such as human, drug, or arms) are most common in here?",
        "Over the past five years, how have levels of gang-related crime and trafficking in here changed, and what local economic, political, or law enforcement factors have contributed to those trends?",
        "What concrete signs—such as graffiti patterns, public incidents, or unusual property use—indicate active gang or trafficking operations in this community?",
        "Based on available crime statistics and case outcomes, how successful have local law enforcement initiatives and community-based programs been at reducing gang activity and trafficking in here?",
      ],
      War: [
        "What are the current active conflicts or tensions in this region, and how have they evolved over the past decade?",
        "Which foreign powers or international actors are directly or indirectly involved in the conflict here, and what are their strategic interests?",
        "How do local ethnic, religious, or political divisions contribute to ongoing instability in this area?",
        "What is the likelihood that local conflicts here could escalate into a broader regional or global confrontation?",
        "How do global economic pressures, such as sanctions or resource competition, influence the conflict in this area?",
      ],
      Retirement: [
        "Does the place actively serve older adults? What share of residents are 60+/65+? Is the population aging?",
        "Is the built environment senior-friendly and accessible?",
        "How strong are mobility, social inclusion, and caregiver supports?",
        "Can seniors access affordable, reliable healthcare? How many primary care clinics/geriatric specialists per capita? Wait times? Emergency care proximity?",
        "Are housing and daily living costs manageable on fixed incomes?",
      ],
    };

    type TopicKey = keyof typeof topicQuestions;

    const form = document.getElementById("topic-form") as HTMLFormElement;
    const selectedTopics: TopicKey[] = Array.from(
      form?.querySelectorAll("input[name='topic']:checked") || []
    ).map((checkbox) => (checkbox as HTMLInputElement).value as TopicKey);

    // const selectedTopicsInnerHTML: string[] = Array.from(
    //   form?.querySelectorAll("input[name='topic']:checked") || []
    // ).map((checkbox) => {
    //   const label = form?.querySelector(
    //     `label[for='${(checkbox as HTMLInputElement).id}']`
    //   );
    //   return label ? label.innerHTML.trim() : "";
    // });
    // const retryData: Record<string, string> = {};
    const selectedTopicsText: string[] = Array.from(
      form?.querySelectorAll("input[name='topic']:checked") || []
    ).map((checkbox) => {
      const label = checkbox.closest("label"); // Find the nearest label wrapping the checkbox
      return label?.textContent?.trim() || "";
    });

    if (selectedTopics.length === 0) {
      alert("Please select at least one topic.");
      return;
    }

    if (drawnItems.current.getLayers().length === 0) {
      alert(
        "Please draw a polygon or circle first before generating a report."
      );
      return;
    }

    try {
      //Report tab not visible
      const fileIcons = document.querySelector(
        ".fa-file"
      ) as HTMLElement | null;
      const reportTab = document.getElementById("reporttab");

      if (reportTab) {
        reportTab.style.display = "block";
      }

      if (fileIcons) {
        fileIcons.style.visibility = "visible";
        fileIcons.style.cursor = "pointer";
      }

      setIsLoading(true); // Start loading
      setIsReportGenerated(true); // ✅ Enable download button after report is generated
      // 1. Capture and upload screenshot only if geometry changed
      let cloudinaryUrl = lastCloudinaryUrlRef.current;
      const currentGeoJSON = JSON.stringify(drawnItems.current.toGeoJSON());
      const geometryChanged = currentGeoJSON !== previousGeometryRef.current;

      if (geometryChanged) {
        // Clear previous analysis if geometry changes
        previousAnalysisRef.current = {};
        previousGeometryRef.current = currentGeoJSON;
      }

      if (screenshoter) {
        if (geometryChanged) {
          const image = await screenshoter.takeScreen("image");
          cloudinaryUrl = await uploadImageToCloudinary(image);
          lastCloudinaryUrlRef.current = cloudinaryUrl;

          // Update shapeData with the new Cloudinary URL
          setShapeData((prevData) => ({
            ...prevData,
            type: prevData?.type ?? "polygon", // Ensure type is always defined
            cloudinaryUrl: cloudinaryUrl ?? prevData?.cloudinaryUrl, // Preserve previous URL
          }));
        } else {
          setShapeData((prevData) => ({
            ...prevData,
            type: prevData?.type ?? "polygon",
            cloudinaryUrl:
              lastCloudinaryUrlRef.current ??
              prevData?.cloudinaryUrl ??
              cloudinaryUrl,
          }));
        }
      }

      // 2. Determine which topics need analysis
      const existingTopics = Object.keys(previousAnalysisRef.current);
      const newTopics = selectedTopics.filter(
        (t) => !existingTopics.includes(t)
      );
      const topicsToAnalyze = geometryChanged ? selectedTopics : newTopics;

      let mergedAnalysis = { ...previousAnalysisRef.current };

      if (topicsToAnalyze.length > 0) {
        // Helper function to generate a prompt for a single topic

        const generatePromptForTopic = (topic: string) => {
          // promptCount= promptCount++
          let prompt = `You are a [Role]. Provide a structured analysis of the neighborhoods within the polygon or circle shown in the image. Your response should have at least 2-3 detailed paragraphs per topic and be data-driven, well-structured, and backed by **credible sources** such as government databases, academic studies, or trusted news outlets. The center coordinates for this location is [Location Coordinates].

          ## **Format for Each Topic**
          **Topic: [Topic Name]**  
          **Analysis:** [Detailed insights, trends, and data points]  
          **Sources:**  
          - [Source Name] ([Year]). [Title]. [URL]  
          - [Source Name] ([Year]). [Title]. [URL]  
          
          ### **Example Topics**
          #### **Demographics**
          - What are the key demographic trends over the past decade?
          - How diverse is the population in terms of ethnicity, socioeconomic background, and languages spoken?
          - What is the age distribution, and how does it impact local policies, businesses, and community dynamics?
          - How do migration patterns affect population growth and demographics?
          - Are there notable disparities in income, education, or employment rates?
          
          ### **Important Instructions**
          - Ensure **each claim or data point** is directly cited with a **source URL**.
          - **URLs must be valid and formatted correctly** (e.g., no trailing punctuation, correct protocol).
          - Format citations consistently using "[Source Name] ([Year]). [Title]. [URL]".
          - Use **plain text formatting** for clarity.
          `;

          if (topicQuestions[topic]) {
            const topicRoles = {
              Demographics: "Demographic Analyst",
              Environment: "Climate and Sustainability Analyst",
              Business: "Business and Economic Analyst",
              Healthcare: "Healthcare Expert",
              Education: "Education Policy Specialist",
              Housing: "Urban Planning and Housing Analyst",
              Proximity: "Geospatial and Urban Mobility Specialist",
              Work: "Labor Market and Employment Analyst",
              Family: "Sociologist and Family Studies Expert",
              Lifestyle: "Lifestyle and Consumer Behavior Analyst",
              Community: "Community Development Specialist",
              Safety: "Public Safety and Crime Analyst",
              Immigration: "Immigration Policy Analyst",
              Gun: "Firearms Policy and Crime Analyst",
              Race: "Social Justice and Equity Analyst",
              Pets: "Animal Welfare and Pet Industry Specialist",
              Technology: "Technology and Innovation Analyst",
              Entertainment: "Cultural and Entertainment Industry Analyst",
              Freedom: "Human Rights and Civil Liberties Expert",
              Transportation: "Transportation and Infrastructure Specialist",
              Tax: "Tax Policy and Economic Analyst",
              Expat: "Expat Lifestyle and International Relocation Consultant",
              Governance: "Public Policy and Governance Expert",
              Cultural: "Cultural Studies and Anthropology Expert",
              Recreation: "Substance Use and Drug Policy Specialist",
              Sustainability: "Sustainability and Environmental Policy Expert",
              Uniqueness: "Geographical and Cultural Heritage Expert",
              Wildlife: "Wildlife Biologist",
              Pollution:
                "Environmental Toxicologist and Public Health Specialist",
              Geopolitics: "Geopolitical Analyst and Foreign Policy Expert",
              Colonial: "Historian, Political Scientist and Anthropologist ",
              Poverty: "Economist and Sociologist",
              BRICS: "International Trade Analyst",
              Future: "Urban Planner and Economic Development Specialist",
              Happiness: "Social Scientist",
              CurrencyUSD: "Cost-of-Living and CPI Analyst",
              Gangs: "Local law enforcement Detective",
              War: "Conflict Analysts and Political Risk Analyst",
              Retirement: " Aging Life Care Professional",
            };

            prompt += `Topic: ${topic}\n`;
            prompt += "Questions:\n";
            topicQuestions[topic].forEach((question: any, index: number) => {
              prompt += `${index + 1}. ${question}\n`;
            });
            prompt += "\n"; // Line break for clarity

            let topicforRole = topic as keyof typeof topicRoles;

            let role = topicRoles[topicforRole];
            const locationPrompt = centerCoordinates;

            prompt = prompt.replace("[Role]", role); // Replaces only the first occurrence of "[Role]"
            prompt = prompt.replace("[Location Coordinates]", locationPrompt); // Replaces only the first occurrence of "[Role]"
          }

          prompt +=
            `Provide analysis in exactly this format:\n` +
            `"Topic: ${topic}\nAnalysis: [Your analysis here]"\n\n` +
            "Use exactly one line break between topics. Do not use markdown.";
          console.log(prompt);
          return prompt;
        };

        // Initialize analysis storage
        mergedAnalysis = { ...previousAnalysisRef.current };

        let previousResponseCount = Object.keys(mergedAnalysis).length;
        //Retry Logic
        const fetchWithRetry = async (
          prompt: string,
          cloudinaryUrl: string,
          maxRetries = 3,
          delayMs = 30000 // Wait 30 seconds before retrying
        ): Promise<string> => {
          for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
              console.log(
                `Attempt ${attempt + 1} to fetch response for: ${prompt}`
              );
             
              const chatResponse = await fetchChatResponseWithImage(
                prompt,
                cloudinaryUrl
              );
              console.log(
                `Response received on attempt ${attempt + 1}:`,
                chatResponse
              );
              return chatResponse; // Exit if successful
            } catch (error) {
              console.error(
                `Error processing attempt ${attempt + 1} for ${prompt}:`,
                error
              );
              if (attempt < maxRetries) {
                console.log(`Retrying in ${delayMs / 1000} seconds...`);
                await new Promise((resolve) => setTimeout(resolve, delayMs));
              } else {
                console.log("Max retries reached. Returning error message.");
              }
            }
          }
          return "Error retrieving analysis."; // Return this only if all retries fail
        };

        let keyCount =
          topicsToAnalyze.length + Object.keys(mergedAnalysis).length;

        // Process each topic separately
        for (const topic of selectedTopics) {
          // Topic count reset

          const prompt = generatePromptForTopic(topic);

          try {
            // Fetch response for the specific topic
            let chatResponse = await fetchChatResponseWithImage(
              prompt,
              cloudinaryUrl
            );

            // Parse and merge the analysis

            let newAnalysis = parseChatResponse(chatResponse);
            let analysisText = newAnalysis[topic];

            if (!analysisText || analysisText.trim() === "") {
              // Retry fetching the response using fetchWithRetry
              chatResponse = await fetchWithRetry(prompt, cloudinaryUrl);
              newAnalysis = parseChatResponse(chatResponse);
              analysisText = newAnalysis[topic];
            }
            // If analysis is still empty after retries, set a fallback message
            if (analysisText && analysisText.trim() !== "") {
              mergedAnalysis[topic] = analysisText;
            } else {
              mergedAnalysis[topic] = analysisText;
              console.log(`No analysis Available for ${topic}`);
            }

            // Response Counter
            const generateReportCalc = document.getElementById("loading-timer");
            let mergedlengthUpdate = Object.keys(mergedAnalysis).length;
            let responseCounter =
              keyCount -
              previousResponseCount -
              (keyCount - mergedlengthUpdate);

            if (generateReportCalc) {
              generateReportCalc.innerHTML = `&nbsp; &nbsp; Generating Report...\n Processed ${responseCounter} of ${topicsToAnalyze.length} topics`;
            }
          } catch (error) {
            console.error(`Final failure for ${topic}:`, error);
            mergedAnalysis[topic] = "Please Retry Analysis.";
          }
        }

        // Save the final merged analysis
        previousAnalysisRef.current = mergedAnalysis;

        generateCollapsibleSections(
          selectedTopics,
          mergedAnalysis,
          selectedTopicsText
        );
      }

      // 6. Generate/update UI sections

      setShapeData((prevData) => ({
        ...prevData,
        type: "circle" as const, // Ensure "circle" is recognized correctly
        mergedAnalysis: JSON.stringify(mergedAnalysis), // Convert object to a string
      }));

      // 7. Switch to results tab
      const tabLink = document.querySelector(
        "a[href='#tab-3']"
      ) as HTMLAnchorElement;
      tabLink?.click();
    } catch (error) {
      console.error("Report generation failed:", error);
      alert("Failed to generate report. Please check the console for details.");
    } finally {
      setIsLoading(false); // Always stop loading
    }
  };

  const handleDownloadReport = () => {
    if (!isReportGenerated) {
      console.error("Error: No shape data available!");
      return;
    }

    if (window.innerWidth <= 575) {
      GenerateMobilePDFReport(shapeData);
    } else {
      GeneratePDFReport(shapeData);
    }
  };

  function PegmanOnce() {
    const map = useMap();
    const disposeRef = useRef<null | (() => void)>(null);

    useEffect(() => {
      if (disposeRef.current) return; // prevent double-add (StrictMode/dev)
      disposeRef.current = attachPegman(map, { position: "bottomright" });
      return () => {
        disposeRef.current?.();
        disposeRef.current = null;
      };
    }, [map]);

    return null;
  }
  // useEffect(() => {
  //   openPanel();
  // }, []);
  // openPanel();

  return (
    <div
      ref={mapRef}
      style={{
        position: "relative",
        height: "100vh",
        width: "100%",
      }}
    >
      <AnimatePresence>
        <SplashScreen />
      </AnimatePresence>

      <Analytics />
      <button
        onClick={openPanel}
        id="openpanelbutton"
        style={{
          position: "absolute",
          top: "10px",
          left: "50px",
          zIndex: 1000,
          padding: "10px",
          background: "#fff",
          border: "1px solid #ccc",
          cursor: "pointer",
          transition: "left 0.3s ease",
          display: "block",
          borderRadius: "5px",
        }}
      >
        Open Panel
      </button>
      <select
        title="selectDropdown"
        className="select-dropdown"
        onChange={handleBaseMapChange}
      >
        {/* <select
        onChange={handleBaseMapChange}
        style={{
          position: "absolute",
          top: "55px",
          left: "50px",
          zIndex: 1000,
          padding: "5px",
          background: "#fff",
          border: "1px solid #ccc",
          cursor: "pointer",
          transition: "left 0.3s ease",
          borderRadius: "5px",
        }}
      > */}
        {baseMaps.map((baseMap) => (
          <option key={baseMap.label} value={baseMap.url}>
            {baseMap.label}
          </option>
        ))}
      </select>
      {/* Sidebar HTML */}
      <div
        id="sidebar"
        className="leaflet-sidebar"
        // style={{ width: "300px", display: "none" }}
        style={{ width: "400px", display: "none" }}
      >
        <div className="leaflet-sidebar-tabs">
          <ul role="tablist">
            <li>
              <a href="#tab-1" role="tab">
                <i className="fa fa-info"></i>
              </a>
            </li>
            <li>
              <a href="#tab-2" role="tab">
                <i className="fa fa-list"></i>
              </a>
            </li>
            <li>
              <a id="reporttab" href="#tab-3" role="tab">
                <i className="fa fa-file"></i>
              </a>
            </li>
          </ul>
        </div>
        <div className="leaflet-sidebar-content">
          <div id="tab-1" className="leaflet-sidebar-pane">
            <h1 className="leaflet-sidebar-header">Information</h1>
          </div>
          <div id="tab-2" className="leaflet-sidebar-pane">
            <h1 className="leaflet-sidebar-header">Select Topics</h1>
            <div className="container">
              <div className="checkbox-list">
                <form id="topic-form">
                  <label>
                    <input
                      style={{
                        display: "none",
                      }}
                      id="selectAll"
                    />{" "}
                  </label>
                  <br />
                  <h3 className=" topicheading popularTopics ">
                    Popular Topics
                  </h3>
                  <label>
                    <input type="checkbox" name="topic" value="Uniqueness" />{" "}
                    Uniqueness and Perception
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Demographics" />{" "}
                    Demographics
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Housing" />{" "}
                    Housing and Income
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Safety" /> Safety
                    and Crime
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Environment" />{" "}
                    Climate and Environment
                  </label>
                  <br />

                  <h3 className=" topicheading">Personal and Social Factors</h3>

                  <label>
                    <input type="checkbox" name="topic" value="Family" /> Family
                    Needs
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Lifestyle" />{" "}
                    Lifestyle
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Community" />{" "}
                    Community
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Retirement" />{" "}
                    Senior Care and Retirement
                  </label>
                  <br />

                  <h3 className=" topicheading">Economy and Work</h3>
                  <label>
                    <input type="checkbox" name="topic" value="Business" />{" "}
                    Business
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Work" /> Work
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Tax" /> Tax
                    Policy
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Expat" />{" "}
                    Expatriation and Digital Nomadism
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="CurrencyUSD" />{" "}
                    Currency (USD)
                  </label>
                  <br />

                  <h3 className=" topicheading">Health and Environment</h3>
                  <label>
                    <input type="checkbox" name="topic" value="Healthcare" />{" "}
                    Healthcare
                  </label>
                  <br />

                  <label>
                    <input
                      type="checkbox"
                      name="topic"
                      value="Sustainability"
                    />{" "}
                    Sustainability and Environmental Policy
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Pollution" />{" "}
                    Environmental Pollution And Chronic Illnesses
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Wildlife" />{" "}
                    Wildlife
                  </label>
                  <br />

                  <h3 className=" topicheading">Housing and Infrastructure</h3>

                  <label>
                    <input type="checkbox" name="topic" value="Proximity" />{" "}
                    Proximity and Accessibility
                  </label>
                  <br />
                  <label>
                    <input
                      type="checkbox"
                      name="topic"
                      value="Transportation"
                    />{" "}
                    Transportation and Infrastructure
                  </label>
                  <br />

                  <h3 className=" topicheading">Safety and Governance</h3>

                  <label>
                    <input type="checkbox" name="topic" value="Gun" /> Gun and
                    Self Defense Laws
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Gangs" />{" "}
                    Organized Crime
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Race" />{" "}
                    Race-Related Violence
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Governance" />{" "}
                    Governance and Public Policy
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Immigration" />{" "}
                    Internal Immigration Policy
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="War" /> Local and
                    Global Conflict
                  </label>
                  <br />

                  <h3 className=" topicheading">Education and Knowledge</h3>
                  <label>
                    <input type="checkbox" name="topic" value="Education" />{" "}
                    Education and Literacy
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Freedom" />{" "}
                    Freedom of Speech and Journalism
                  </label>
                  <br />

                  <h3 className=" topicheading">Culture and Recreation</h3>
                  <label>
                    <input type="checkbox" name="topic" value="Cultural" />{" "}
                    Cultural and Religious
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Entertainment" />{" "}
                    Fun and Entertainment
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Recreation" />{" "}
                    Recreation Drug Use Policy
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Pets" /> Pets
                  </label>
                  <br />

                  <h3 className=" topicheading">Geopolitics and History</h3>
                  <label>
                    <input type="checkbox" name="topic" value="Geopolitics" />{" "}
                    Geopolitics and Foreign Relations
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Colonial" />{" "}
                    Colonial Interference and History
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Poverty" />{" "}
                    Income Disparity and Poverty
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="BRICS" />{" "}
                    Relationship with BRICS
                  </label>
                  <h3 className=" topicheading">Technology</h3>
                  <label>
                    <input type="checkbox" name="topic" value="Technology" />{" "}
                    Technology
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Future" /> Future
                    Growth
                  </label>
                  <br />
                  <h3 className=" topicheading">Happiness and Relationships</h3>
                  <label>
                    <input type="checkbox" name="topic" value="Happiness" />{" "}
                    Perception of Happiness
                  </label>
                  <br />
                  <label>
                    <input type="checkbox" name="topic" value="Relationships" />{" "}
                    Relationships
                  </label>
                  <br />

                  {isLoading && (
                    <div id="loading-overlay">
                      <div className="loading-spinner"></div>
                      <p id="loading-timer" className="ml-4 text-gray-700">
                        &nbsp;&nbsp; Generating report...Please be patient
                      </p>
                    </div>
                  )}
                </form>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "20px",
                    marginTop: "20px",
                  }}
                >
                  <button
                    type="button"
                    id="generate-report"
                    style={{
                      borderRadius: "5px",
                      // zIndex: 1000,
                      padding: "10px 10px",
                      background: "#fff",
                      border: "1px solid blue",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    Generate Report
                  </button>

                  <button
                    id="download-report"
                    onClick={handleDownloadReport}
                    disabled={!isReportGenerated} // ✅ Disabled until report is generated
                    style={{
                      borderRadius: "5px",
                      // zIndex: 1000,
                      padding: "10px 10px",
                      background: isReportGenerated ? "#007bff" : "#ccc",
                      color: isReportGenerated ? "white" : "gray",
                      border: "1px solid #ccc",
                      cursor: isReportGenerated ? "pointer" : "not-allowed",
                      fontSize: "14px",
                    }}
                  >
                    Download Report
                  </button>
                </div>

                <br />
              </div>
            </div>
          </div>

          <div id="tab-3" className="leaflet-sidebar-pane">
            <h1 className="leaflet-sidebar-header">Results</h1>
          </div>
        </div>
      </div>
      <MapContainer
        center={mapCenter}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
      >
        <InitializeMap
          initializeMap={initializeMap}
          mapInstanceRef={mapInstanceRef}
        />

        <TileLayer
          url={currentBaseMap}
          attribution="&copy; OpenStreetMap contributors"
        />
        <SearchControl />

        <GeojsonGroupedControl
          items={[
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "All Global Cities",
              url: "/geo/GlobalCities.geojson",
            },
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "Global Leaders",
              url: "/geo/GlobalLeaders.geojson",
            },
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "Regional Leaders",
              url: "/geo/RegionalLeaders.geojson",
              defaultOn: true,
            },
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "Cultural Capital",
              url: "/geo/CulturalCapital.geojson",
            },
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "Sustainable Cities",
              url: "/geo/SustainableCities.geojson",
            },
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "Industrial Hubs",
              url: "/geo/IndustrialHubs.geojson",
            },
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "Legacy Cities",
              url: "/geo/LegacyCities.geojson",
            },
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "Developing Megacities",
              url: "/geo/DevelopingMega.geojson",
            },
            {
              group: "Top 1000 Cities (Oxford Economics)",
              name: "Emerging Standouts",
              url: "/geo/EmergingStandouts.geojson",
            },
          ]}
        />
        {/* <RasterGeoTiffOverlay
          url="/geo/tas_CrossingYear_CMIP6_ssp245.tif"
          name="tas_CrossingYear (CMIP6 ssp245)"
          group="Rasters"
          defaultOn={false}
          opacity={0.85}
          domain={[2005, 2101]} // <- matches the legend numbers in your screenshot
          legendMode="bar" // default; can omit
          decimals={0}
        /> */}

        <RasterGeoTiffOverlay
          url="/geo/Tornadoes.tif"
          name="Tornados"
          group="Rasters"
          opacity={0.6}
          legendMode="bar"
          decimals={0}
        />
        <RasterGeoTiffOverlay
          url="/geo/Hurricanes.tif"
          name="Hurricanes"
          group="Rasters"
          opacity={0.6}
          legendMode="bar"
          decimals={0}
        />
        <RasterGeoTiffOverlay
          url="/geo/StrongestQuakes.tif"
          name="Strong Earthquakes (≥5.5)"
          group="Rasters"
          opacity={0.6}
          legendMode="bar"
          decimals={0}
        />
        <RasterGeoTiffOverlay
          url="/geo/Tsunamis.tif"
          name="Tsunamis"
          group="Rasters"
          opacity={0.6}
          legendMode="bar"
          decimals={0}
        />
        <RasterGeoTiffOverlay
          url="/geo/tas_CrossingYear_CMIP6_ssp245.tif"
          name="TAS Crossing Year (CMIP6 SSP245)"
          group="Rasters"
          // defaultOn
          opacity={0.85}
          legendMode="bar" // default; can omit
          decimals={0}
        />
        <RasterGeoTiffOverlay
          url="/geo/tas_CrossingYear_CMIP6_ssp585.tif"
          name="TAS Crossing Year (CMIP6 SSP585)"
          group="Rasters"
          opacity={0.85}
          legendMode="bar" // default; can omit
          decimals={0}
        />
        <RasterGeoTiffOverlay
          url="/geo/Hailstorms.tif"
          name="Hailstorms"
          group="Rasters"
          opacity={0.6}
          legendMode="bar"
          decimals={0}
        />
        <div
          id="searchBarid"
          className="searchBarClass"
          // className=" my-answer my-answer-panel"
        >
          <SearchBar />
        </div>
        <PegmanOnce />
        <MiniMapControl />
      </MapContainer>
    </div>
  );
};

export default Map;
